import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaCompass, FaCar, FaSync, FaLayerGroup } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/Pages.css';
import { GetFromApi } from '../utils/api';
import socketService from '../utils/socket';
import { getRouteFromOSRM, calculateBearing } from '../utils/routing';

import carIcon from '../images/car.png';

// Map rotation component
const MapRotator = ({ heading, enabled }) => {
  const map = useMap();
  
  React.useEffect(() => {
    const container = map.getContainer();
    if (enabled && heading !== null) {
      container.style.transform = `rotate(${-heading}deg)`;
      container.style.transition = 'transform 0.5s ease';
    } else {
      container.style.transform = 'rotate(0deg)';
    }
  }, [heading, enabled, map]);
  
  return null;
};

// Animated marker component
const AnimatedMarker = ({ position, icon, children }) => {
  const markerRef = useRef(null);
  const prevPosRef = useRef(position);

  useEffect(() => {
    if (position[0] === prevPosRef.current[0] && position[1] === prevPosRef.current[1]) {
      return;
    }

    const marker = markerRef.current;
    if (!marker) return;

    const start = prevPosRef.current;
    const end = position;
    const startTime = Date.now();
    const duration = 1500;
    let frameId;

    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      
      marker.setLatLng([
        start[0] + (end[0] - start[0]) * ease,
        start[1] + (end[1] - start[1]) * ease
      ]);

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      } else {
        prevPosRef.current = end;
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [position]);

  return <Marker ref={markerRef} position={prevPosRef.current} icon={icon}>{children}</Marker>;
};

const MapRotate = () => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [rotateEnabled, setRotateEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLayer, setMapLayer] = useState('street');
  const [showLayerModal, setShowLayerModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [roadRoutes, setRoadRoutes] = useState({});

  const fetchRoadRoute = useCallback(async (vehicleId, positions) => {
    if (positions.length < 2) return;
    try {
      const route = await getRouteFromOSRM(positions);
      setRoadRoutes(prev => ({ ...prev, [vehicleId]: route }));
    } catch (e) {
      console.warn('Route fetch failed:', e);
    }
  }, []);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const response = await GetFromApi('/vehicles');
      if (response.success) {
        const vehiclesWithPosition = await Promise.all(response.data.map(async (v, i) => {
          const lat = v.lat ? parseFloat(v.lat) : 23.0225 + i * 0.01;
          const lng = v.lng ? parseFloat(v.lng) : 72.5714 + i * 0.01;
          
          let historyCoords = [[lat, lng]];
          try {
            if (v.device_id) {
              const histRes = await GetFromApi(`/history/${v.device_id}`);
              if (Array.isArray(histRes)) {
                const validPoints = histRes
                  .filter(r => r.latitude && r.longitude && parseFloat(r.latitude) !== 0)
                  .map(r => [parseFloat(r.latitude), parseFloat(r.longitude)])
                  .reverse();
                
                const uniquePoints = [];
                for (const p of validPoints) {
                  if (uniquePoints.length === 0) {
                    uniquePoints.push(p);
                  } else {
                    const last = uniquePoints[uniquePoints.length - 1];
                    const dist = Math.abs(p[0] - last[0]) + Math.abs(p[1] - last[1]);
                    if (dist > 0.0001) {
                      uniquePoints.push(p);
                    }
                  }
                }
                
                if (uniquePoints.length > 0) {
                  historyCoords = uniquePoints;
                }
              }
            }
          } catch (err) {
            console.error("Failed to load vehicle history:", err);
          }
          
          return {
            ...v,
            heading: v.heading || 0,
            position: [lat, lng],
            routeHistory: historyCoords,
            odometer: v.odometer || 0,
            int_voltage: v.int_voltage || 0,
            ext_voltage: v.ext_voltage || 0
          };
        }));

        // Snap routes to OSRM roads
        vehiclesWithPosition.forEach(v => {
          if (v.routeHistory && v.routeHistory.length >= 2) {
            fetchRoadRoute(v.id, v.routeHistory);
          }
        });

        setVehicles(vehiclesWithPosition);
      }
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();

    socketService.connect();
    socketService.on('vehicle:location', (data) => {
      setVehicles(prev => prev.map(v => {
        if (v.id === data.vehicleId) {
          const newPos = [data.lat, data.lng];
          const latDiff = Math.abs(data.lat - v.position[0]);
          const lngDiff = Math.abs(data.lng - v.position[1]);
          const hasMovedSignificantly = latDiff > 0.0001 || lngDiff > 0.0001;

          const bearing = (data.speed > 3 && hasMovedSignificantly) ? calculateBearing(v.position, newPos) : (v.heading || 0);
          const history = hasMovedSignificantly
            ? [...(v.routeHistory || []), newPos].slice(-30)
            : (v.routeHistory || []);
          
          if (hasMovedSignificantly && history.length >= 2) {
            fetchRoadRoute(v.id, history);
          }
          
          // Use txn-based movement (SIWI protocol: A/B/F=moving, E/G=stopped)
          const isRunning = data.isMoving !== undefined ? data.isMoving : (data.speed > 3);
          // Use device-reported GPS bearing when moving; freeze heading when stopped
          const finalBearing = (isRunning && hasMovedSignificantly)
            ? (data.heading || bearing)
            : (v.heading || 0);
          
          return {
            ...v,
            position: newPos,
            speed: data.speed,
            heading: finalBearing,
            status: isRunning ? 'active' : 'maintenance',
            ignition: data.ignition !== undefined ? data.ignition : v.ignition,
            routeHistory: history,
            odometer: data.odometer !== undefined ? data.odometer : v.odometer,
            int_voltage: data.int_voltage !== undefined ? data.int_voltage : v.int_voltage,
            ext_voltage: data.ext_voltage !== undefined ? data.ext_voltage : v.ext_voltage
          };
        }
        return v;
      }));
    });

    return () => socketService.off('vehicle:location');
  }, [fetchRoadRoute]);

  const mapLayers = [
    { id: 'street', name: 'Street Map (OpenStreetMap)', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
    { id: 'satellite', name: 'Satellite View (Esri)', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' },
    { id: 'hybrid', name: 'Hybrid View (Google)', url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}' },
    { id: 'terrain', name: 'Terrain Map', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png' },
    { id: 'dark', name: 'Dark Mode Map (CartoDB)', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' }
  ];

  const createVehicleIcon = (vehicle) => {
    const isRunning = vehicle.status === 'active' || vehicle.status === 'running';
    const color = isRunning ? '#22C55E' : '#EF4444';
    return L.divIcon({
      className: 'custom-vehicle-marker',
      html: `
        <div style="position: relative; width: 55px; height: 55px;">
          <div style="
            position: absolute; 
            top: 50%; left: 50%; 
            transform: translate(-50%, -50%) rotate(${vehicle.heading}deg);
            transition: transform 0.5s ease;
          ">
            <img src="${carIcon}" alt="Car" style="width: 40px; height: 40px;" />
          </div>
          <div style="
            position: absolute; top: -15px; left: 50%; 
            transform: translateX(-50%);
            background: ${color}; color: white;
            border-radius: 10px; padding: 2px 8px; 
            font-size: 10px; font-weight: bold;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            white-space: nowrap;
          ">
            ${vehicle.speed || 0} km/h
          </div>
        </div>
      `,
      iconSize: [55, 55],
      iconAnchor: [27, 27],
      popupAnchor: [0, -27]
    });
  };

  const selectedData = selectedVehicle ? vehicles.find(v => v.id === selectedVehicle) : null;
  const mapCenter = selectedData?.position || [23.0225, 72.5714];
  const currentHeading = selectedData?.heading || 0;

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <h4 className="page-title">Map Rotate View</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchVehicles} disabled={isLoading} style={{ borderRadius: '8px' }}>
            {isLoading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
          <Button variant="light" onClick={() => setShowLayerModal(true)} style={{ borderRadius: '8px' }}>
            <FaLayerGroup className="me-2" /> Layers
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={3} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0 fw-semibold">Vehicles ({vehicles.length})</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3 p-3 rounded" style={{ backgroundColor: '#f8fafc' }}>
                <div className="d-flex align-items-center">
                  <FaCompass className="me-2 text-primary" />
                  <span className="fw-medium">Auto Rotate</span>
                </div>
                <Form.Check 
                  type="switch" id="rotate-switch"
                  checked={rotateEnabled}
                  onChange={(e) => setRotateEnabled(e.target.checked)}
                />
              </div>

              {isLoading ? (
                <div className="text-center py-4"><Spinner size="sm" /></div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {vehicles.map(v => (
                    <div 
                      key={v.id}
                      className={`d-flex align-items-center p-3 mb-2 rounded ${selectedVehicle === v.id ? 'border border-primary' : 'border'}`}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedVehicle === v.id ? 'rgba(59, 130, 246, 0.05)' : 'white'
                      }}
                      onClick={() => setSelectedVehicle(v.id)}
                    >
                      <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: 44, height: 44, borderRadius: 10,
                          backgroundColor: selectedVehicle === v.id ? '#3B82F6' : '#EEF2F8'
                        }}
                      >
                        <FaCar color={selectedVehicle === v.id ? 'white' : '#3B82F6'} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{v.number}</div>
                        <div className="d-flex gap-2 align-items-center">
                          <span style={{ 
                            width: 8, height: 8, borderRadius: '50%', 
                            backgroundColor: v.speed > 0 ? '#22C55E' : '#EF4444',
                            display: 'inline-block'
                          }} />
                          <small className="text-muted">{v.speed || 0} km/h</small>
                          <small className="text-muted">• {Math.round(v.heading)}°</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-semibold">
                Rotate Map {selectedVehicle && <span className="text-muted fw-normal">- {currentHeading}°</span>}
              </h6>
              <div 
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: '#f8fafc', border: '2px solid #e2e8f0'
                }}
              >
                <FaCompass 
                  size={20} color="#3B82F6"
                  style={{ 
                    transform: rotateEnabled ? `rotate(${currentHeading}deg)` : 'rotate(0deg)',
                    transition: 'transform 0.5s ease'
                  }}
                />
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ height: '550px', overflow: 'hidden' }}>
                <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }} key={selectedVehicle}>
                  <TileLayer url={mapLayers.find(l => l.id === mapLayer)?.url || mapLayers[0].url} />
                  <MapRotator heading={currentHeading} enabled={rotateEnabled && selectedVehicle !== null} />
                  
                  {vehicles.map(v => (
                    <React.Fragment key={v.id}>
                      {(roadRoutes[v.id] || v.routeHistory)?.length > 1 && (
                        <Polyline
                          positions={roadRoutes[v.id] || v.routeHistory}
                          pathOptions={{
                            color: v.speed > 0 ? '#3B82F6' : '#EF4444',
                            weight: 4, opacity: 0.8,
                            lineCap: 'round', lineJoin: 'round'
                          }}
                        />
                      )}
                      <AnimatedMarker position={v.position} icon={createVehicleIcon(v)}>
                        <Popup>
                          <div className="p-1">
                            <h6 className="mb-1">{v.number}</h6>
                            <p className="mb-1">
                              <strong>Status:</strong>{' '}
                              <span style={{ color: (v.status === 'active' || v.status === 'running') ? '#22C55E' : '#EF4444' }}>
                                {v.status === 'active' || v.status === 'running' ? 'Running' : 'Stopped'}
                              </span>
                            </p>
                            <p className="mb-1"><strong>Speed:</strong> {v.speed || 0} km/h</p>
                            <p className="mb-1"><strong>Heading:</strong> {Math.round(v.heading)}°</p>
                            <p className="mb-1"><strong>Odometer:</strong> {parseFloat(v.odometer || 0).toFixed(1)} km</p>
                            {v.int_voltage > 0 && <p className="mb-1"><strong>Battery Voltage:</strong> {parseFloat(v.int_voltage).toFixed(1)} V</p>}
                            {v.ext_voltage > 0 && <p className="mb-0"><strong>Mains Voltage:</strong> {parseFloat(v.ext_voltage).toFixed(1)} V</p>}
                          </div>
                        </Popup>
                      </AnimatedMarker>
                    </React.Fragment>
                  ))}
                </MapContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showLayerModal} onHide={() => setShowLayerModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Map Layers</Modal.Title></Modal.Header>
        <Modal.Body>
          {mapLayers.map(layer => (
            <div 
              key={layer.id}
              className={`d-flex align-items-center p-3 mb-2 rounded ${mapLayer === layer.id ? 'border border-primary bg-light' : 'border'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => { setMapLayer(layer.id); setShowLayerModal(false); }}
            >
              <FaLayerGroup className={`me-3 ${mapLayer === layer.id ? 'text-primary' : 'text-muted'}`} />
              <span className={mapLayer === layer.id ? 'fw-semibold' : ''}>{layer.name}</span>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default MapRotate;
